import type { TextDirection } from "./direction.js";

// bidi-js v1.0.3은 타입 선언(@types/bidi-js)을 제공하지 않는다. ambient `declare module`은 실제 패키지
// 파일이 resolve되면 무시되므로, import만 억제하고 사용 경계에서 로컬 타입으로 좁힌다.
// (API 출처: node_modules/bidi-js/dist/bidi.mjs — default export = factory.)
// @ts-expect-error — bidi-js ships no bundled type declarations
import bidiFactory from "bidi-js";

interface BidiEmbeddingLevels {
  readonly levels: Uint8Array;
}
interface Bidi {
  getEmbeddingLevels(text: string, baseDirection?: "ltr" | "rtl" | "auto"): BidiEmbeddingLevels;
  getReorderedString(text: string, levels: BidiEmbeddingLevels): string;
}

// factory는 1회만 생성 — 내부 가변 상태가 없어 전역 재사용이 안전하다.
const bidi = (bidiFactory as unknown as () => Bidi)();

/**
 * RTL 텍스트를 표시 순서(visual order)로 재배열한다 — Unicode Bidi 알고리즘(UAX #9, bidi-js).
 * - LTR이면 변환 없이 그대로 반환(no-op).
 * - RTL이면 논리순 → 표시순 reorder. 라틴/숫자 run은 LTR로 보존되고 아랍/히브리 run만 뒤집힌다.
 *
 * ⚠ 적용 주의(opt-in): 브라우저 Canvas `fillText`는 **단일 라인** 텍스트의 Bidi/shaping을 자체 처리한다.
 *   따라서 단순 라벨에는 이 reorder가 불필요하며 오히려 이중 적용 시 깨질 수 있다.
 *   Phaser word-wrap 등으로 라인이 분할되어 Bidi run이 끊기는 경우에만 사전 reorder가 필요하다.
 *   `DirectionalText`는 기본적으로 적용하지 않고 `applyBidi` 옵션으로만 호출한다.
 *
 * shaping(아랍어 글자 연결형 변환)은 이 함수의 책임이 아니며 Canvas 렌더에 위임한다.
 */
export function reorderForDirection(text: string, dir: TextDirection): string {
  if (dir !== "rtl" || text.length === 0) return text;
  const levels = bidi.getEmbeddingLevels(text, "rtl");
  return bidi.getReorderedString(text, levels);
}
